/**
 * Aging Bucket Color Utilities
 *
 * Provides consistent color coding for dates based on days until/overdue.
 * Used across billing queue aging buckets and follow-up date displays.
 *
 * Color Scheme (UAT 2026-06-24 — Walid: "due today isn't a bad thing"):
 * The urgency semantic for date TEXT is a calm 3-color scale:
 * - 🔴 Soft red (#e06666): Overdue (past due date)  — needs attention, not an alarm
 * - 🔵 Blue (#3b82f6): Due today               — today's work, not an alarm
 * - 🟢 Green (#16a34a): Due any future day      — on track, no rush
 *
 * The aggregate aging buckets keep their finer day-bands for FILTERING
 * (1-4 / 5-7 / 8-10 / 11+), but those future bands all render in the green
 * family — see AGING_BUCKET_PALETTE. Only "overdue" is red and only "today"
 * is blue.
 */

/** Per-bucket fill colors for the mini-bar segments and filter chips. Overdue
 *  red, today blue, the four future bands in graded greens (nearer = deeper). */
export const AGING_BUCKET_PALETTE: Record<string, string> = {
  overdue: '#e06666',   // 🔴 Soft red (muted — calmer than a vivid alarm red)
  today: '#3b82f6',     // 🔵 Blue
  '1-4days': '#16a34a', // 🟢 Green (deep)
  '5-7days': '#22c55e', // 🟢 Green
  '8-10days': '#4ade80',// 🟢 Green (light)
  '11+days': '#86efac', // 🟢 Green (lightest)
};

/**
 * Get the color for a date based on days until it.
 * 3-color urgency scale (overdue / today / future). For TEXT — uses a single
 * readable green for every future day (light greens fail contrast on white).
 * @param daysUntil - Number of days until the target date (negative if overdue, 0 = today)
 * @returns Hex color string
 */
export const getAgingBucketColor = (daysUntil: number): string => {
  if (daysUntil < 0) return '#e06666'; // 🔴 Overdue — soft, muted red (not an alarm)
  if (daysUntil === 0) return '#3b82f6'; // 🔵 Due today (primary blue)
  return '#16a34a'; // 🟢 Due in the future (success green)
};

/**
 * Get the label for an aging bucket based on days until a date.
 * Keeps the finer day-bands so filter buttons stay descriptive.
 * @param daysUntil - Number of days until the target date (negative if overdue, 0 = today)
 * @returns Human-readable label for the aging bucket
 */
export const getAgingBucketLabel = (daysUntil: number): string => {
  if (daysUntil < 0) return 'Overdue';
  if (daysUntil === 0) return 'Today';
  if (daysUntil <= 4) return '1-4 Days';
  if (daysUntil <= 7) return '5-7 Days';
  if (daysUntil <= 10) return '8-10 Days';
  return '11+ Days';
};

/**
 * Get the emoji icon for a date based on days until it.
 * 3-color semantic: 🔴 overdue, 🔵 today, 🟢 any future day.
 * @param daysUntil - Number of days until the target date (negative if overdue, 0 = today)
 * @returns Emoji character
 */
export const getAgingBucketEmoji = (daysUntil: number): string => {
  if (daysUntil < 0) return '🔴';
  if (daysUntil === 0) return '🔵';
  return '🟢';
};
